import { Suspense } from 'react'
import { Canvas } from '@react-three/fiber'
import { Grid, OrbitControls, GizmoHelper, GizmoViewport } from '@react-three/drei'
import { OrientationFrame } from './components/OrientationFrame'
import { SkeletonModel } from './components/SkeletonModel'
import { SensorInfoPanel } from './components/SensorInfoPanel'
import { useResolvedTheme, useUiStore } from '@/state/uiStore'

const SCENE_LIGHTING = {
  ambientIntensity: 0.95,
  keyLightIntensity: 4.65,
}

// Theme-specific values for Three.js primitives that don't go through Tailwind
const SCENE_COLORS = {
  dark: {
    bg: '#05070d',
    cell: '#1c2233',
    section: '#2c3448',
    gizmoLabel: '#e9edf5',
    gizmoAxisColors: ['#ff5c6c', '#2fd67a', '#6fb1ff'] as [string, string, string],
  },
  light: {
    bg: '#f3f5f9',
    cell: '#e2e7f0',
    section: '#cdd5e3',
    gizmoLabel: '#0a0e17',
    gizmoAxisColors: ['#c81e30', '#0e8a4a', '#1b66dc'] as [string, string, string],
  },
}

/**
 * SceneViewport is the main 3D canvas.
 * It provides the XY ground plane, camera controls, and the avatar slot.
 */
const SCENE_MODEL_URLS: Record<string, string> = {
  default: '/assets/art_mannequin.glb',
  health: '/assets/FullSkeleton.glb',
  robotics: '/assets/robot_neo.glb',
}

export function SceneViewport() {
  const scene = useUiStore((s) => s.scene)
  const resolvedTheme = useResolvedTheme()
  const colors = SCENE_COLORS[resolvedTheme]
  const modelUrl = SCENE_MODEL_URLS[scene] ?? SCENE_MODEL_URLS.default

  return (
    <div className="relative h-full w-full">
      <Canvas
        camera={{ position: [1.5, 1.5, 0], fov: 70 }}
        style={{ background: colors.bg }}
        gl={{ antialias: true, alpha: false }}
        dpr={[1, 2]}
      >
        <color attach="background" args={[colors.bg]} />

        <ambientLight intensity={SCENE_LIGHTING.ambientIntensity} />
        <directionalLight position={[2, 5, 0]} intensity={SCENE_LIGHTING.keyLightIntensity} />

        {/* XY-plane grid */}
        <Grid
          args={[20, 20]}
          cellSize={1}
          cellThickness={0.5}
          cellColor={colors.cell}
          sectionSize={5}
          sectionThickness={1}
          sectionColor={colors.section}
          fadeDistance={30}
          fadeStrength={1}
          followCamera={false}
          infiniteGrid
        />

        <OrientationFrame />

        <Suspense fallback={null}>
          <SkeletonModel key={modelUrl} url={modelUrl} applyArmsDownPose={scene === 'default'} />
        </Suspense>

        <OrbitControls makeDefault enableDamping dampingFactor={0.5} target={[0, 1, 0]} />

        <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
          <GizmoViewport axisColors={colors.gizmoAxisColors} labelColor={colors.gizmoLabel} />
        </GizmoHelper>
      </Canvas>

      <SensorInfoPanel />
    </div>
  )
}
